// Phase 36 Batch W — runtime PWA meta-tag failsafe.
//
// We already ship the iOS meta tags from /public/index.html as part of
// Batch V, but Batch W's smoke test on prod found them absent — root
// cause was a stale deploy bundle, not the source. To prevent a future
// runtime-injection (e.g. an analytics tool or a server-rendered HTML
// rewriter at the CDN edge) from clobbering the static head, this hook
// re-asserts the iOS / Android installability meta tags on every app
// mount.
//
// All operations are idempotent — we only insert or update if the tag
// is missing or has a different content. We never duplicate.
//
// Cheap: runs once at mount, no listeners. Removing the hook returns
// the page to relying purely on /public/index.html.

const REQUIRED_META = [
  { name: "apple-mobile-web-app-capable", content: "yes" },
  { name: "mobile-web-app-capable", content: "yes" },
  { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
  { name: "apple-mobile-web-app-title", content: "RunIdea" },
  { name: "theme-color", content: "#16a34a" },
];

const REQUIRED_LINKS = [
  { rel: "manifest", href: "/manifest.webmanifest" },
  { rel: "apple-touch-icon", href: "/icons/apple-touch-icon.png", sizes: "180x180" },
];

function _upsertMeta({ name, content }) {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (el) {
    if (el.getAttribute("content") !== content) el.setAttribute("content", content);
    return;
  }
  el = document.createElement("meta");
  el.setAttribute("name", name);
  el.setAttribute("content", content);
  document.head.appendChild(el);
}

function _upsertLink({ rel, href, sizes }) {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (el) {
    if (el.getAttribute("href") !== href) el.setAttribute("href", href);
    if (sizes && el.getAttribute("sizes") !== sizes) el.setAttribute("sizes", sizes);
    return;
  }
  el = document.createElement("link");
  el.setAttribute("rel", rel);
  el.setAttribute("href", href);
  if (sizes) el.setAttribute("sizes", sizes);
  document.head.appendChild(el);
}

export function ensurePWAMetaTags() {
  if (typeof document === "undefined") return;
  for (const m of REQUIRED_META) _upsertMeta(m);
  for (const l of REQUIRED_LINKS) _upsertLink(l);
}

// Convenience for one-shot mount-time invocation from App.js.
export default ensurePWAMetaTags;
